<template>
    <div class="animated fadeIn master-form-page">
        <!-- Back Button -->
        <div class="mb-3">
            <button type="button" class="btn btn-back" @click="$router.go(-1)">
                <i class="fa fa-angle-left mr-1"></i> Back
            </button>
        </div>

        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <div v-else class="master-form-card">
            <!-- Header Title & Counter -->
            <div class="d-flex flex-wrap align-items-start justify-content-between mb-4 pb-2 border-bottom">
                <div>
                    <h4 class="form-page-title mb-1">
                        Atribut {{ selectedJenisBarangName || 'Jenis Barang' }}
                    </h4>
                    <p class="text-muted small mb-0">
                        Atribut ini dipakai saat penaksir mengisi data barang pada transaksi gadai.
                    </p>
                </div>
                <div class="attribute-counter-badge mt-2 mt-md-0">
                    <strong>{{ atributList.length }}</strong> atribut · <strong class="text-warning-dark">{{ mandatoryCount }}</strong> wajib
                </div>
            </div>

            <b-form @submit.prevent="saveAllAttributes">
                <!-- 1. DATA SELEKSI (Tipe Pinjaman & Jenis Barang) -->
                <div class="form-section-block mb-4">
                    <b-row>
                        <b-col md="6" class="mb-3">
                            <label class="field-label font-weight-bold">TIPE PINJAMAN</label>
                            <b-form-select 
                                v-model="form.tipePinjamanId" 
                                :options="optTipePinjaman" 
                                value-field="id" 
                                text-field="name" 
                                class="form-control-custom w-100"
                                @change="onTipePinjamanChange"
                                required
                            >
                                <template v-slot:first>
                                    <b-form-select-option :value="null" disabled>-- Pilih Tipe Pinjaman --</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-col>
                        <b-col md="6" class="mb-3">
                            <label class="field-label font-weight-bold">JENIS BARANG</label>
                            <b-form-select 
                                v-model="form.jenisBarangId" 
                                :options="optJenisBarangFiltered" 
                                value-field="id" 
                                text-field="name" 
                                class="form-control-custom w-100"
                                :disabled="!form.tipePinjamanId"
                                @change="onJenisBarangChange"
                                required
                            >
                                <template v-slot:first>
                                    <b-form-select-option :value="null" disabled>-- Pilih Jenis Barang --</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-col>
                    </b-row>

                    <!-- MERK BARANG -->
                    <div class="mt-2">
                        <label class="field-label font-weight-bold">MERK BARANG</label>
                        <div class="merk-tag-input-container">
                            <div class="merk-chips-wrapper">
                                <span 
                                    v-for="(merk, idx) in form.merkList" 
                                    :key="idx" 
                                    class="merk-tag-pill"
                                >
                                    {{ merk }}
                                    <i class="fa fa-times ml-2 remove-tag-icon" @click="removeMerkTag(idx)" title="Hapus merk"></i>
                                </span>
                                <input 
                                    type="text" 
                                    v-model="newMerkInput" 
                                    class="merk-tag-text-input" 
                                    placeholder="Tambah merk lalu tekan Enter"
                                    @keydown.enter.prevent="addMerkTag"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 2. INLINE ATTRIBUTE BUILDER FORM (Keluaran dari Modal) -->
                <div class="attribute-builder-panel mb-4">
                    <div class="builder-panel-header d-flex align-items-center justify-content-between mb-3">
                        <span class="builder-panel-title">
                            <i class="fa" :class="editingIndex !== null ? 'fa-pencil' : 'fa-plus-circle'"></i>
                            {{ editingIndex !== null ? 'Ubah Atribut' : 'Tambah Atribut Baru' }}
                        </span>
                        <button 
                            v-if="editingIndex !== null" 
                            type="button" 
                            class="btn btn-sm btn-link text-muted p-0" 
                            @click="cancelEditAttribute"
                        >
                            Batal Edit
                        </button>
                    </div>

                    <b-row>
                        <!-- LABEL -->
                        <b-col md="6" class="mb-3">
                            <label class="field-label">LABEL <span class="text-danger">*</span></label>
                            <b-form-input 
                                v-model="attrForm.label" 
                                placeholder="no. imei/sn/no. polisi/no. mesin/no. rangka"
                                class="form-control-custom"
                            ></b-form-input>
                        </b-col>

                        <!-- LENGTH -->
                        <b-col md="6" class="mb-3" v-if="attrForm.type === 'Freetext' || attrForm.type === 'Numeric'">
                            <label class="field-label">LENGTH (MAKS KARAKTER)</label>
                            <b-form-input 
                                v-model.number="attrForm.length" 
                                type="number"
                                placeholder="Contoh: 15, 20"
                                class="form-control-custom"
                                min="1"
                            ></b-form-input>
                        </b-col>

                        <!-- OPTIONS (untuk Select) -->
                        <b-col md="6" class="mb-3" v-if="attrForm.type === 'Single Select' || attrForm.type === 'Multi Select'">
                            <label class="field-label">OPSI PILIHAN (Pisahkan dengan koma)</label>
                            <b-form-input 
                                v-model="attrForm.optionsText" 
                                placeholder="Contoh: 64GB, 128GB, 256GB"
                                class="form-control-custom"
                            ></b-form-input>
                        </b-col>
                    </b-row>

                    <!-- TIPE (Pill selector) -->
                    <div class="mb-3">
                        <label class="field-label d-block">TIPE <span class="text-danger">*</span></label>
                        <div class="d-flex flex-wrap gap-2 type-pill-group">
                            <button 
                                type="button" 
                                v-for="t in typeOptions" 
                                :key="t"
                                class="btn btn-type-pill"
                                :class="{ 'active': attrForm.type === t }"
                                @click="attrForm.type = t"
                            >
                                {{ t }}
                            </button>
                        </div>
                    </div>

                    <!-- MANDATORY CHECKBOX & SUBMIT ATTRIBUTE BUTTON -->
                    <div class="d-flex flex-wrap align-items-center justify-content-between pt-2">
                        <b-form-checkbox 
                            v-model="attrForm.isMandatory" 
                            class="custom-form-checkbox"
                        >
                            Mandatory (Wajib Diisi)
                        </b-form-checkbox>

                        <button 
                            type="button" 
                            class="btn btn-add-attr-action"
                            @click="saveAttributeToTable"
                        >
                            <i class="fa" :class="editingIndex !== null ? 'fa-check mr-1' : 'fa-plus mr-1'"></i>
                            {{ editingIndex !== null ? 'Perbarui Atribut' : 'Tambah Atribut' }}
                        </button>
                    </div>
                </div>

                <!-- 3. TABEL DAFTAR ATRIBUT -->
                <div class="table-card-inner mb-2">
                    <b-table 
                        hover 
                        :fields="attrTableFields" 
                        :items="atributList" 
                        responsive 
                        class="master-atribut-table mb-0" 
                        show-empty
                    >
                        <template v-slot:empty>
                            <div class="text-center py-4 text-muted">
                                Belum ada atribut yang ditambahkan. Gunakan form di atas untuk menambahkan atribut.
                            </div>
                        </template>

                        <!-- Slot NO -->
                        <template v-slot:cell(no)="row">
                            <span class="cell-no">{{ formatNumber(row.index + 1) }}</span>
                        </template>

                        <!-- Slot LABEL -->
                        <template v-slot:cell(label)="row">
                            <span class="cell-label-text">{{ row.item.label }}</span>
                            <div v-if="row.item.options && row.item.options.length > 0" class="text-muted small">
                                Opsi: {{ row.item.options.join(', ') }}
                            </div>
                        </template>

                        <!-- Slot TIPE -->
                        <template v-slot:cell(type)="row">
                            <span class="badge-type-tag">{{ row.item.type }}</span>
                        </template>

                        <!-- Slot LENGTH -->
                        <template v-slot:cell(length)="row">
                            <span class="cell-length">{{ row.item.length ? row.item.length : '—' }}</span>
                        </template>

                        <!-- Slot MANDATORY -->
                        <template v-slot:cell(mandatory)="row">
                            <span class="badge-mandatory-wajib" v-if="row.item.isMandatory">Wajib</span>
                            <span class="badge-mandatory-opsional" v-else>Opsional</span>
                        </template>

                        <!-- Slot AKSI -->
                        <template v-slot:cell(action)="row">
                            <div class="d-flex align-items-center justify-content-center gap-2">
                                <button 
                                    type="button" 
                                    class="btn btn-action-ubah mr-1"
                                    @click="editAttributeItem(row.index)"
                                    title="Ubah Atribut"
                                >
                                    Ubah
                                </button>
                                <button 
                                    type="button" 
                                    class="btn btn-action-hapus"
                                    @click="deleteAttributeItem(row.index)"
                                    title="Hapus Atribut"
                                >
                                    Hapus
                                </button>
                            </div>
                        </template>
                    </b-table>
                </div>

                <p class="text-muted small mt-2 mb-4">
                    * Length hanya berlaku untuk tipe freetext dan numeric.
                </p>

                <!-- 4. FOOTER ACTIONS -->
                <div class="form-actions-footer">
                    <button 
                        type="button" 
                        class="btn btn-cancel" 
                        @click="$router.go(-1)"
                    >
                        Kembali
                    </button>
                    <button 
                        type="submit" 
                        class="btn btn-submit"
                        :disabled="isSubmitting"
                    >
                        <b-spinner v-if="isSubmitting" small class="mr-1"></b-spinner>
                        Simpan Atribut
                    </button>
                </div>
            </b-form>
        </div>
    </div>
</template>

<script>
export default {
    name: 'tambah-atribut-jenis-barang',
    data() {
        return {
            initStatus: 0,
            isSubmitting: false,
            editingIndex: null,
            newMerkInput: '',
            deletedAtributIds: [],

            form: {
                tipePinjamanId: 1,
                jenisBarangId: 1,
                merkList: ['Samsung', 'Apple', 'Xiaomi', 'Oppo']
            },

            // Form penambahan atribut inline
            attrForm: {
                id: null,
                atributId: null,
                label: '',
                nameKey: '',
                type: 'Freetext',
                length: null,
                isMandatory: false,
                optionsText: ''
            },

            typeOptions: ['Freetext', 'Numeric', 'Date', 'Single Select', 'Multi Select'],

            // List Atribut yang ada pada tabel
            atributList: [
                {
                    id: null,
                    atributId: null,
                    label: 'No. IMEI',
                    nameKey: 'no_imei',
                    type: 'Freetext',
                    dataType: 'Freetext',
                    length: 15,
                    lengthLimit: 15,
                    isMandatory: true,
                    mandatory: true,
                    options: []
                },
                {
                    id: null,
                    atributId: null,
                    label: 'No. SN',
                    nameKey: 'no_sn',
                    type: 'Freetext',
                    dataType: 'Freetext',
                    length: 20,
                    lengthLimit: 20,
                    isMandatory: false,
                    mandatory: false,
                    options: []
                },
                {
                    id: null,
                    atributId: null,
                    label: 'Kapasitas Memori',
                    nameKey: 'kapasitas_memori',
                    type: 'Single Select',
                    dataType: 'Single Select',
                    length: null,
                    lengthLimit: null,
                    isMandatory: true,
                    mandatory: true,
                    options: ['64GB', '128GB', '256GB', '512GB']
                },
                {
                    id: null,
                    atributId: null,
                    label: 'Kelengkapan',
                    nameKey: 'kelengkapan',
                    type: 'Multi Select',
                    dataType: 'Multi Select',
                    length: null,
                    lengthLimit: null,
                    isMandatory: false,
                    mandatory: false,
                    options: ['Dus', 'Charger', 'Kabel Data', 'Earphone', 'Nota']
                },
                {
                    id: null,
                    atributId: null,
                    label: 'Tanggal Pembelian',
                    nameKey: 'tanggal_pembelian',
                    type: 'Date',
                    dataType: 'Date',
                    length: null,
                    lengthLimit: null,
                    isMandatory: false,
                    mandatory: false,
                    options: []
                }
            ],

            attrTableFields: [
                { 
                    key: 'no', 
                    label: 'NO', 
                    thClass: 'text-left align-middle', 
                    tdClass: 'text-left align-middle',
                    thStyle: { width: '8%', minWidth: '50px' }
                },
                { 
                    key: 'label', 
                    label: 'LABEL', 
                    thClass: 'text-left align-middle', 
                    tdClass: 'text-left align-middle',
                    thStyle: { width: '32%', minWidth: '160px' }
                },
                { 
                    key: 'type', 
                    label: 'TIPE', 
                    thClass: 'text-left align-middle', 
                    tdClass: 'text-left align-middle',
                    thStyle: { width: '22%', minWidth: '130px' }
                },
                { 
                    key: 'length', 
                    label: 'LENGTH', 
                    thClass: 'text-center align-middle', 
                    tdClass: 'text-center align-middle',
                    thStyle: { width: '12%', minWidth: '80px' }
                },
                { 
                    key: 'mandatory', 
                    label: 'MANDATORY', 
                    thClass: 'text-center align-middle', 
                    tdClass: 'text-center align-middle',
                    thStyle: { width: '12%', minWidth: '90px' }
                },
                { 
                    key: 'action', 
                    label: 'AKSI', 
                    thClass: 'text-center align-middle', 
                    tdClass: 'text-center align-middle',
                    thStyle: { width: '14%', minWidth: '130px' }
                }
            ],

            // Master data dropdowns
            optTipePinjaman: [
                { id: 1, name: 'Gadai Elektronik' },
                { id: 2, name: 'Gadai Kendaraan' }
            ],

            optAllJenisBarang: [
                { id: 1, tipePinjamanId: 1, name: 'Handphone', merks: ['Samsung (H)', 'Apple (H)', 'Xiao mi (H)', 'Oppo (H)'] },
                { id: 2, tipePinjamanId: 1, name: 'Laptop', merks: ['ACER / ASUS / TOSHIBA / SONY / FUJITSU (L)', 'Macbook (L)', 'HP/LENOVO/DELL/SAMSUNG (L)'] },
                { id: 13, tipePinjamanId: 2, name: 'Motor', merks: ['Honda (GB)', 'Yamaha (GB)', 'Suzuki (GB)', 'Kawasaki (GB)'] },
                { id: 14, tipePinjamanId: 2, name: 'Mobil', merks: ['Toyota', 'Honda', 'Daihatsu', 'Suzuki', 'Mitsubishi'] }
            ]
        }
    },
    computed: {
        optJenisBarangFiltered() {
            if (!this.form.tipePinjamanId) return []
            return this.optAllJenisBarang.filter(item => item.tipePinjamanId === this.form.tipePinjamanId)
        },
        selectedJenisBarangName() {
            const found = this.optAllJenisBarang.find(item => item.id === this.form.jenisBarangId)
            return found ? found.name : ''
        },
        mandatoryCount() {
            return this.atributList.filter(item => item.isMandatory || item.mandatory).length
        }
    },
    methods: {
        generateNameKey(label) {
            if (!label) return ''
            return label
                .toLowerCase()
                .trim()
                .replace(/[^a-z0-9]+/g, '_')
                .replace(/^_+|_+$/g, '')
        },

        init() {
            this.initStatus = 0
            let routeId = this.$route.params.id || this.$route.query.jenisId

            // Load tipe pinjaman & jenis barang dari API
            let pTipePinjaman = this.$axios.get('jenis-pinjaman/').then(res => {
                if (Array.isArray(res.data) && res.data.length > 0) {
                    this.optTipePinjaman = res.data
                }
            }).catch(() => {})

            let pJenisAtribut = this.$axios.get('barang-jenis-atribut/').then(res => {
                let list = Array.isArray(res.data) ? res.data : (res.data && res.data.data ? res.data.data : [])
                if (list.length > 0) {
                    this.optAllJenisBarang = list.map(item => ({
                        id: item.jenisBarangId,
                        tipePinjamanId: item.jenisPinjaman ? item.jenisPinjaman.id : 1,
                        tipePinjamanName: item.jenisPinjaman ? item.jenisPinjaman.name : '',
                        name: item.jenisBarangName,
                        merks: Array.isArray(item.merkBarang) ? item.merkBarang.map(m => (typeof m === 'object' && m !== null ? m.name : m)) : []
                    }))

                    if (routeId) {
                        let jId = parseInt(routeId, 10)
                        let foundJenis = this.optAllJenisBarang.find(j => j.id === jId)
                        if (foundJenis) {
                            this.form.tipePinjamanId = foundJenis.tipePinjamanId
                            this.form.jenisBarangId = foundJenis.id
                            this.form.merkList = [...foundJenis.merks]
                        }
                    } else if (this.optAllJenisBarang.length > 0 && !this.form.jenisBarangId) {
                        this.form.tipePinjamanId = this.optAllJenisBarang[0].tipePinjamanId
                        this.form.jenisBarangId = this.optAllJenisBarang[0].id
                        this.form.merkList = [...this.optAllJenisBarang[0].merks]
                    }
                }
            }).catch(() => {})

            Promise.all([pTipePinjaman, pJenisAtribut]).finally(() => {
                this.initStatus = 1
                if (this.form.jenisBarangId) {
                    this.fetchDetailAtribut(this.form.jenisBarangId)
                }
            })
        },

        fetchDetailAtribut(jenisBarangId) {
            if (!jenisBarangId) return
            this.$axios.get(`barang-jenis-atribut/${jenisBarangId}`).then(res => {
                let data = res.data
                let list = []
                if (Array.isArray(data)) {
                    list = data
                } else if (data && Array.isArray(data.atribut)) {
                    list = data.atribut
                } else if (data && Array.isArray(data.attributes)) {
                    list = data.attributes
                } else if (data && Array.isArray(data.atributList)) {
                    list = data.atributList
                } else if (data && Array.isArray(data.data)) {
                    list = data.data
                }

                if (list.length > 0) {
                    this.atributList = list.map(item => ({
                        id: item.id || item.atributId || null,
                        atributId: item.atributId || item.id || null,
                        label: item.label || '',
                        nameKey: item.nameKey || this.generateNameKey(item.label),
                        type: item.dataType || item.type || 'Freetext',
                        dataType: item.dataType || item.type || 'Freetext',
                        length: item.lengthLimit !== undefined ? item.lengthLimit : (item.length || null),
                        lengthLimit: item.lengthLimit !== undefined ? item.lengthLimit : (item.length || null),
                        isMandatory: item.mandatory !== undefined ? Boolean(item.mandatory) : Boolean(item.isMandatory),
                        mandatory: item.mandatory !== undefined ? Boolean(item.mandatory) : Boolean(item.isMandatory),
                        options: Array.isArray(item.options) ? item.options : (typeof item.options === 'string' && item.options ? item.options.split(',').map(s => s.trim()).filter(Boolean) : [])
                    }))
                }
            }).catch(() => {
                // Keep default list or empty
            })
        },

        formatNumber(num) {
            return String(num).padStart(2, '0')
        },

        onTipePinjamanChange() {
            this.form.jenisBarangId = null
            this.form.merkList = []
            if (this.optJenisBarangFiltered.length > 0) {
                this.form.jenisBarangId = this.optJenisBarangFiltered[0].id
                this.onJenisBarangChange()
            }
        },

        onJenisBarangChange() {
            const found = this.optAllJenisBarang.find(item => item.id === this.form.jenisBarangId)
            if (found && found.merks) {
                this.form.merkList = [...found.merks]
            } else {
                this.$axios.get(`barang-merk/jenis/${this.form.jenisBarangId}`).then(res => {
                    if (Array.isArray(res.data)) {
                        this.form.merkList = res.data.map(m => (typeof m === 'object' && m !== null ? m.name : m))
                    }
                }).catch(() => {})
            }

            this.deletedAtributIds = []
            this.fetchDetailAtribut(this.form.jenisBarangId)
        },

        addMerkTag() {
            if (!this.newMerkInput || this.newMerkInput.trim() === '') return
            const tag = this.newMerkInput.trim()
            if (!this.form.merkList.includes(tag)) {
                this.form.merkList.push(tag)
            }
            this.newMerkInput = ''
        },

        removeMerkTag(index) {
            this.form.merkList.splice(index, 1)
        },

        saveAttributeToTable() {
            if (!this.attrForm.label || this.attrForm.label.trim() === '') {
                this.$helper.toastErr(this, 'Label atribut wajib diisi!')
                return
            }

            let optionsArr = []
            if (this.attrForm.type === 'Single Select' || this.attrForm.type === 'Multi Select') {
                if (this.attrForm.optionsText) {
                    optionsArr = this.attrForm.optionsText.split(',').map(s => s.trim()).filter(Boolean)
                }
            }

            let nameKey = this.attrForm.nameKey || this.generateNameKey(this.attrForm.label)

            const item = {
                id: this.attrForm.id || null,
                atributId: this.attrForm.atributId || this.attrForm.id || null,
                label: this.attrForm.label.trim(),
                nameKey: nameKey,
                type: this.attrForm.type,
                dataType: this.attrForm.type,
                length: (this.attrForm.type === 'Freetext' || this.attrForm.type === 'Numeric') ? (this.attrForm.length || null) : null,
                lengthLimit: (this.attrForm.type === 'Freetext' || this.attrForm.type === 'Numeric') ? (this.attrForm.length || null) : null,
                isMandatory: this.attrForm.isMandatory,
                mandatory: this.attrForm.isMandatory,
                options: optionsArr
            }

            if (this.editingIndex !== null) {
                const existing = this.atributList[this.editingIndex]
                if (existing && (existing.id || existing.atributId)) {
                    item.id = existing.id || existing.atributId
                    item.atributId = existing.id || existing.atributId
                }
                this.$set(this.atributList, this.editingIndex, item)
                this.$helper.toastSucc(this, `Atribut "${item.label}" berhasil diperbarui!`)
                this.cancelEditAttribute()
            } else {
                this.atributList.push(item)
                this.$helper.toastSucc(this, `Atribut "${item.label}" ditambahkan ke daftar!`)
                this.resetAttrForm()
            }
        },

        editAttributeItem(index) {
            this.editingIndex = index
            const item = this.atributList[index]
            this.attrForm = {
                id: item.id || item.atributId || null,
                atributId: item.atributId || item.id || null,
                label: item.label,
                nameKey: item.nameKey || this.generateNameKey(item.label),
                type: item.type || item.dataType || 'Freetext',
                length: item.lengthLimit !== undefined ? item.lengthLimit : item.length,
                isMandatory: item.mandatory !== undefined ? item.mandatory : item.isMandatory,
                optionsText: item.options ? (Array.isArray(item.options) ? item.options.join(', ') : item.options) : ''
            }
        },

        deleteAttributeItem(index) {
            const deleted = this.atributList[index]
            if (deleted && (deleted.id || deleted.atributId)) {
                this.deletedAtributIds.push(deleted.id || deleted.atributId)
            }
            this.atributList.splice(index, 1)
            if (this.editingIndex === index) {
                this.cancelEditAttribute()
            }
            this.$helper.toastSucc(this, `Atribut "${deleted.label}" berhasil dihapus!`)
        },

        cancelEditAttribute() {
            this.editingIndex = null
            this.resetAttrForm()
        },

        resetAttrForm() {
            this.attrForm = {
                id: null,
                atributId: null,
                label: '',
                nameKey: '',
                type: 'Freetext',
                length: null,
                isMandatory: false,
                optionsText: ''
            }
        },

        saveAllAttributes() {
            if (!this.form.jenisBarangId) {
                this.$helper.toastErr(this, 'Mohon pilih jenis barang terlebih dahulu!')
                return
            }

            if (this.atributList.length === 0) {
                this.$helper.toastErr(this, 'Mohon tambahkan minimal 1 atribut!')
                return
            }

            this.isSubmitting = true
            let payload = new FormData()

            for (let item of this.atributList) {
                // atributId (kirim id jika ada, atau string kosong untuk item baru)
                let aId = item.id || item.atributId || ''
                payload.append('atributId', aId)

                // label
                payload.append('label', item.label ? item.label.trim() : '')

                // nameKey
                payload.append('nameKey', item.nameKey || this.generateNameKey(item.label))

                // dataType
                payload.append('dataType', item.type || item.dataType || 'Freetext')
                
                // lengthLimit (kirim nilai jika ada, atau string kosong agar array index sejajar)
                let len = (item.length !== null && item.length !== undefined && item.length !== '') ? item.length : ''
                payload.append('lengthLimit', len)

                // mandatory
                payload.append('mandatory', Boolean(item.isMandatory || item.mandatory))

                // options (hanya untuk Select, atau string kosong agar array index sejajar)
                let optStr = ''
                if (item.type === 'Single Select' || item.type === 'Multi Select' || item.dataType === 'Single Select' || item.dataType === 'Multi Select') {
                    if (Array.isArray(item.options) && item.options.length > 0) {
                        optStr = item.options.join(',')
                    } else if (typeof item.options === 'string' && item.options) {
                        optStr = item.options
                    }
                }
                payload.append('options', optStr)
            }

            if (this.deletedAtributIds && this.deletedAtributIds.length > 0) {
                for (let dId of this.deletedAtributIds) {
                    payload.append('deleteId', dId)
                }
            }

            this.$axios.post(`barang-jenis-atribut/${this.form.jenisBarangId}`, payload).then(response => {
                this.$helper.setFlash('Atribut jenis barang berhasil disimpan!')
                this.$helper.toastSucc(this, 'Atribut jenis barang berhasil disimpan!')
                setTimeout(() => {
                    this.$router.push('/master/atribut-jenis-barang')
                }, 500)
            }).catch(error => {
                console.error('Gagal menyimpan atribut jenis barang:', error)
                this.$helper.parseError(this, error)
            }).finally(() => {
                this.isSubmitting = false
            })
        }
    },
    created() {
        this.init()
    }
}
</script>

<style lang="scss" scoped>
.master-form-page {
    padding-bottom: 30px;
}

.btn-back {
    background-color: #ffffff;
    border: 1px solid #d8e2dc;
    color: #16302b;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    padding: 6px 14px;
    display: inline-flex;
    align-items: center;
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
        background-color: #f4f7f5;
        border-color: #bac9c2;
        color: #072d28;
    }
}

.master-form-card {
    background-color: #ffffff;
    border: 1px solid #e2e7e3;
    border-radius: 12px;
    padding: 28px 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.form-page-title {
    font-size: 20px;
    font-weight: 700;
    color: #072d28;
    letter-spacing: -0.01em;
}

.attribute-counter-badge {
    font-size: 13px;
    color: #64748b;
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    padding: 6px 14px;
    border-radius: 20px;

    .text-warning-dark {
        color: #b45309;
    }
}

.field-label {
    font-size: 11.5px;
    letter-spacing: 0.05em;
    color: #475569;
    margin-bottom: 6px;
}

.form-control-custom {
    border-radius: 8px;
    border: 1px solid #d8e2dc;
    font-size: 13px;
    color: #16302b;
    padding: 8px 12px;
    height: 40px;
    transition: all 0.2s ease;
    background-color: #ffffff;

    &:focus {
        border-color: #0b3b36;
        box-shadow: 0 0 0 2px rgba(11, 59, 54, 0.12);
        outline: none;
    }

    &::placeholder {
        color: #94a3b8;
    }
}

/* Merk Tag Input */
.merk-tag-input-container {
    border: 1px solid #d8e2dc;
    border-radius: 8px;
    padding: 8px 12px;
    background-color: #ffffff;
    min-height: 48px;
    display: flex;
    align-items: center;
}

.merk-chips-wrapper {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    width: 100%;
}

.merk-tag-pill {
    background-color: #e6f4f1;
    color: #0b3b36;
    border: 1px solid #c8e6df;
    border-radius: 8px;
    padding: 4px 10px;
    font-size: 12.5px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;

    .remove-tag-icon {
        font-size: 11px;
        color: #64748b;
        cursor: pointer;

        &:hover {
            color: #dc2626;
        }
    }
}

.merk-tag-text-input {
    border: none;
    outline: none;
    font-size: 13px;
    color: #16302b;
    flex-grow: 1;
    min-width: 220px;
    padding: 4px 0;

    &::placeholder {
        color: #94a3b8;
    }
}

/* Inline Attribute Builder Panel */
.attribute-builder-panel {
    background-color: #fbfdfc;
    border: 1px dashed #cbd5e1;
    border-radius: 10px;
    padding: 20px;
}

.builder-panel-title {
    font-size: 13.5px;
    font-weight: 700;
    color: #0b3b36;
    letter-spacing: 0.02em;
}

.type-pill-group {
    display: flex;
    gap: 8px;
}

.btn-type-pill {
    background-color: #ffffff;
    border: 1px solid #dcdfe6;
    color: #475569;
    border-radius: 8px;
    padding: 6px 14px;
    font-size: 12.5px;
    font-weight: 500;
    transition: all 0.15s ease;
    cursor: pointer;

    &:hover {
        background-color: #f1f5f9;
        border-color: #cbd5e1;
    }

    &.active {
        background-color: #0b3b36;
        border-color: #0b3b36;
        color: #ffffff;
        font-weight: 600;
    }
}

.btn-add-attr-action {
    background-color: #e6f4f1;
    color: #0b3b36;
    border: 1px solid #c2e5dc;
    border-radius: 8px;
    padding: 7px 18px;
    font-size: 13px;
    font-weight: 600;
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
        background-color: #0b3b36;
        border-color: #0b3b36;
        color: #ffffff;
    }
}

.custom-form-checkbox {
    font-size: 13px;
    color: #1e293b;
    font-weight: 500;
}

/* Table */
.table-card-inner {
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid #edf2f0;
    background: #ffffff;
}

.master-atribut-table {
    width: 100%;

    ::v-deep {
        thead th {
            background-color: #fafcfb !important;
            color: #5e7a75 !important;
            font-size: 11px !important;
            font-weight: 700 !important;
            letter-spacing: 0.6px !important;
            text-transform: uppercase !important;
            border-top: none !important;
            border-bottom: 1px solid #eef2f1 !important;
            border-left: none !important;
            border-right: none !important;
            padding: 14px 16px !important;
            white-space: nowrap !important;
        }

        tbody td {
            padding: 14px 16px !important;
            font-size: 13.5px !important;
            color: #334155 !important;
            border-top: 1px solid #f1f5f4 !important;
            border-bottom: none !important;
            border-left: none !important;
            border-right: none !important;
            vertical-align: middle !important;
        }

        tbody tr:hover {
            background-color: #fcfdfd !important;
        }
    }
}

.cell-no {
    color: #64748b;
    font-weight: 500;
    font-size: 13px;
}

.cell-label-text {
    font-weight: 600;
    color: #1e293b;
    font-size: 13.5px;
}

.cell-length {
    color: #475569;
    font-size: 13px;
}

.badge-type-tag {
    display: inline-block;
    background-color: #f1f5f9;
    color: #334155;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 3px 10px;
    font-size: 11.5px;
    font-weight: 500;
}

.badge-mandatory-wajib {
    display: inline-block;
    background-color: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 11.5px;
    font-weight: 600;
}

.badge-mandatory-opsional {
    display: inline-block;
    background-color: #f1f5f9;
    color: #64748b;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 11.5px;
    font-weight: 500;
}

.btn-action-ubah {
    background-color: #ffffff;
    color: #334155;
    border: 1px solid #dcdfe6;
    border-radius: 6px;
    padding: 4px 12px;
    font-size: 12px;
    font-weight: 500;
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
        background-color: #f8fafc;
        border-color: #cbd5e1;
        color: #0b3b36;
    }
}

.btn-action-hapus {
    background-color: #ffffff;
    color: #dc2626;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 4px 12px;
    font-size: 12px;
    font-weight: 500;
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
        background-color: #fef2f2;
        border-color: #fca5a5;
        color: #b91c1c;
    }
}

/* Footer Actions */
.form-actions-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    border-top: 1px solid #edf2f0;
    padding-top: 18px;
    margin-top: 16px;

    .btn-submit {
        background-color: #0b3b36;
        border: 1px solid #0b3b36;
        color: #ffffff;
        border-radius: 8px;
        padding: 9px 24px;
        font-size: 13.5px;
        font-weight: 600;
        transition: all 0.2s ease;
        cursor: pointer;

        &:hover:not(:disabled) {
            background-color: #082d29;
            border-color: #082d29;
            box-shadow: 0 2px 8px rgba(11, 59, 54, 0.2);
        }
    }

    .btn-cancel {
        background-color: #ffffff;
        border: 1px solid #d8e2dc;
        color: #16302b;
        border-radius: 8px;
        padding: 9px 20px;
        font-size: 13.5px;
        font-weight: 500;
        transition: all 0.2s ease;
        cursor: pointer;

        &:hover {
            background-color: #f4f7f5;
            border-color: #bac9c2;
            color: #072d28;
        }
    }
}
</style>
